import { useEffect, useMemo } from 'react'

import { ApolloProvider } from '@apollo/client'
import { Provider as ReduxProvider } from 'react-redux'
import { RouterProvider } from 'react-router-dom'

import {
  hasOtherUsableAccounts,
  isRestrictedNoAccessViewer,
} from 'utils/noAccessAccount'
import { LaunchDarklyWrapper } from 'utils/sdks/launchDarkly.v2'
import MixpanelProvider from 'utils/sdks/mixpanel'

import { getApolloClient } from 'graphql/client'
import { useFetchCurrentUser } from 'hooks/fetchCurrentUser'
import { getReduxStore } from 'store'

import LoaderOverlay from 'components/Loader/LoaderOverlay'
import { LOGOUT } from 'routes/paths'

import { config } from './config'
import { useAuth } from './context/AuthProvider'
import { createAppRouter } from './router'

const reduxDevtoolsExtension =
  window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()

const App = () => {
  const { isInitialized, logout } = useAuth()
  const store = getReduxStore(reduxDevtoolsExtension)
  const client = useMemo(
    () =>
      getApolloClient({ uri: config.urls.backend, dispatch: store.dispatch }),
    [store.dispatch],
  )
  const { userInfo, loading, error } = useFetchCurrentUser()
  const isNoAccess = isRestrictedNoAccessViewer(userInfo)
  const canSwitchAccount = hasOtherUsableAccounts(userInfo)

  useEffect(() => {
    if (loading || !isInitialized) {
      return
    }
    if (isNoAccess && !canSwitchAccount) {
      logout(window.location.origin + LOGOUT)
    }
  }, [loading, isInitialized, isNoAccess, canSwitchAccount, logout])

  if (loading || !isInitialized) {
    return <LoaderOverlay />
  }

  if (isNoAccess && !canSwitchAccount) {
    return <LoaderOverlay />
  }

  if (error) {
    // FIXME: this does not work with legacy pages
    // return (
    //   <div className={styles.routerOutlet}>
    //     <RouterPage path="" title="home">
    //       <ErrorPage />
    //     </RouterPage>
    //   </div>
    // )
    return <></>
  }

  const router = createAppRouter(userInfo)

  return (
    <ApolloProvider client={client}>
      <ReduxProvider store={store}>
        <MixpanelProvider userInfo={userInfo}>
          <LaunchDarklyWrapper userInfo={userInfo}>
            <RouterProvider router={router} />
          </LaunchDarklyWrapper>
        </MixpanelProvider>
      </ReduxProvider>
    </ApolloProvider>
  )
}

export default App
